import { ref } from 'vue'
import { listNoteCategories, type SupportNoteCategory, type SupportNoteCategoryV1 } from '@/api/supportNotes'

export interface CategoryColor {
    background: string
    color: string
}

/**
 * Pill colors per category slug. Colors are a frontend / design-system concern and are
 * intentionally NOT served by the API, so they live here keyed by the known slugs. Any
 * slug the API returns that is missing here falls back to {@link DEFAULT_CATEGORY_COLOR},
 * so a newly added server-side category still renders (just in neutral grey) without an
 * admin deploy.
 */
export const CATEGORY_COLORS: Record<SupportNoteCategory, CategoryColor> = {
    general: { background: 'rgb(240,240,240)', color: 'rgb(84,84,84)' },
    fyi: { background: 'rgb(223,240,252)', color: 'rgb(35,94,150)' },
    troubleshooting: { background: 'rgb(255,240,216)', color: 'rgb(160,88,0)' },
    'known-issue': { background: 'rgb(253,226,226)', color: 'rgb(176,0,32)' },
    'account-access': { background: 'rgb(237,231,246)', color: 'rgb(94,53,177)' },
    'subscription-promo': { background: 'rgb(232,245,233)', color: 'rgb(27,94,32)' },
    'privacy-compliance': { background: 'rgb(236,239,241)', color: 'rgb(55,71,79)' },
    intouch: { background: 'rgb(226,245,235)', color: 'rgb(20,120,74)' },
    hardware: { background: 'rgb(255,243,224)', color: 'rgb(230,81,0)' },
    'smart-routine': { background: 'rgb(225,245,254)', color: 'rgb(2,119,189)' },
    watercare: { background: 'rgb(224,247,250)', color: 'rgb(0,131,143)' },
    waterlab: { background: 'rgb(243,229,245)', color: 'rgb(123,31,162)' }
}

export const DEFAULT_CATEGORY_COLOR: CategoryColor = { background: 'rgb(240,240,240)', color: 'rgb(84,84,84)' }

export function categoryColor(slug: string): CategoryColor {
    return CATEGORY_COLORS[slug as SupportNoteCategory] ?? DEFAULT_CATEGORY_COLOR
}

/**
 * Reactive, module-level cache of the taxonomy fetched from the API. Shared across the
 * notes card and drawer so categories are fetched at most once per session (call
 * {@link loadCategories} to force a refresh).
 */
const categories = ref<SupportNoteCategoryV1[]>([])
const loaded = ref(false)
let inFlight: Promise<SupportNoteCategoryV1[]> | null = null

/** The ordered list of selectable categories (empty until {@link loadCategories} resolves). */
export function noteCategories() {
    return categories
}

/** Fetches the taxonomy once and caches it. Subsequent calls return the cached list. */
export async function loadCategories(force = false): Promise<SupportNoteCategoryV1[]> {
    if (loaded.value && !force) return categories.value
    if (inFlight && !force) return inFlight

    inFlight = listNoteCategories()
        .then((result) => {
            // The API already orders by sortOrder; sort defensively in case that changes.
            categories.value = [...result].sort((a, b) => a.sortOrder - b.sortOrder)
            loaded.value = true
            return categories.value
        })
        .finally(() => {
            inFlight = null
        })

    return inFlight
}

/** Label lookup by slug, built from the fetched taxonomy. */
export function categoryLabelMap(): Map<string, string> {
    return new Map(categories.value.map((c) => [c.slug, c.label]))
}

/**
 * Label for a category slug. Prefers the note's server-provided `categoryLabel`; falls
 * back to the fetched taxonomy, then to the raw slug, so rendering never breaks.
 */
export function categoryLabel(slug: string, serverLabel?: string): string {
    if (serverLabel && serverLabel.length > 0) return serverLabel
    return categoryLabelMap().get(slug) ?? slug
}

/** Sorts notes most-recent-first by createdAt. */
export function sortNotes<T extends { createdAt: string }>(notes: T[]): T[] {
    return [...notes].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
}
